import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Copy } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { BrandIcon, BrandLogo, useBrandLogo } from '../components/brand.js';
import { RecoveryCodes } from '../components/recovery-codes.js';
import { Button, cn, ErrorBanner, Input, Label } from '../components/ui.js';
import { sessionQuery } from '../lib/meta.js';
import { useBranding } from '../lib/theme.js';
import { errorMessage, trpc, trpcClient, type RouterOutputs } from '../lib/trpc.js';

type LoginResult = RouterOutputs['auth']['login'];
type Setup = Extract<LoginResult, { next: 'enroll' }>;
type Enrolled = RouterOutputs['auth']['twoFactor']['enroll'];
type SessionView = RouterOutputs['auth']['twoFactor']['verify'];

export function AuthFrame({
  title,
  wide,
  children,
}: {
  title: string;
  /** For steps with more to show than a form, such as setting up two-factor sign-in. */
  wide?: boolean;
  children: ReactNode;
}) {
  const product = useBranding()?.product_name ?? '';
  const logo = useBrandLogo();
  return (
    <main className="flex min-h-full items-center justify-center bg-gradient-to-b from-brand-soft to-slate-50 px-4 py-12">
      <div className={cn('w-full', wide ? 'max-w-md' : 'max-w-sm')}>
        <div className="mb-6 text-center">
          {logo ? (
            <BrandLogo alt={product} className="mx-auto h-10 w-auto" />
          ) : (
            <>
              <BrandIcon
                name={product}
                className="mx-auto mb-3 size-11"
                tile="rounded-xl text-lg shadow-sm"
              />
              <p className="text-lg font-semibold text-slate-900">{product}</p>
            </>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-surface p-6 shadow-sm">
          <h1 className="mb-4 text-base font-semibold text-slate-900">{title}</h1>
          {children}
        </div>
      </div>
    </main>
  );
}

/** How far signing in has got. */
type Step =
  | { kind: 'password' }
  | { kind: 'code'; challenge: string }
  | { kind: 'enroll'; setup: Setup }
  | { kind: 'recovery_codes'; codes: string[]; session: SessionView };

/**
 * Signing in takes two steps for everyone: the password, then a code from an authenticator
 * app or a recovery code. The first time, the second step sets the app up instead, and the
 * recovery codes that makes are shown once before the app opens.
 */
export function LoginScreen() {
  const search = useSearch({ strict: false }) as { next?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<Step>({ kind: 'password' });
  const restart = () => setStep({ kind: 'password' });
  const enter = async (session: SessionView) => {
    queryClient.setQueryData(sessionQuery.queryKey, session);
    await queryClient.invalidateQueries({ queryKey: trpc.meta.app.queryKey() });
    // Only a path on this site: never somewhere a crafted sign-in link names.
    const next =
      search.next &&
      search.next.startsWith('/') &&
      !search.next.startsWith('//') &&
      !search.next.startsWith('/\\')
        ? search.next
        : '/';
    await navigate({ href: next });
  };
  switch (step.kind) {
    case 'password':
      return (
        <PasswordStep
          email={email}
          onEmail={setEmail}
          onPassed={(result) =>
            setStep(
              result.next === 'enroll'
                ? { kind: 'enroll', setup: result }
                : { kind: 'code', challenge: result.challenge },
            )
          }
        />
      );
    case 'code':
      return <CodeStep challenge={step.challenge} onSignedIn={enter} onRestart={restart} />;
    case 'enroll':
      return (
        <EnrollStep
          setup={step.setup}
          onEnrolled={(result) =>
            setStep({
              kind: 'recovery_codes',
              codes: result.recovery_codes,
              session: result.session,
            })
          }
          onRestart={restart}
        />
      );
    case 'recovery_codes':
      return (
        <SaveCodesStep
          codes={step.codes}
          account={step.session.user.email}
          onContinue={() => enter(step.session)}
        />
      );
  }
}

function PasswordStep({
  email,
  onEmail,
  onPassed,
}: {
  email: string;
  onEmail: (email: string) => void;
  onPassed: (result: LoginResult) => void;
}) {
  const [password, setPassword] = useState('');
  const login = useMutation({
    mutationFn: () => trpcClient.auth.login.mutate({ email, password }),
    onSuccess: onPassed,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate();
  };
  return (
    <AuthFrame title="Sign in">
      <form onSubmit={submit} className="space-y-4">
        {login.error && <ErrorBanner>{errorMessage(login.error)}</ErrorBanner>}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => onEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <Button type="submit" variant="primary" className="w-full" busy={login.isPending}>
          Sign in
        </Button>
      </form>
    </AuthFrame>
  );
}

/** The second step: a code from the app, or one of the recovery codes. */
function CodeStep({
  challenge,
  onSignedIn,
  onRestart,
}: {
  challenge: string;
  onSignedIn: (session: SessionView) => Promise<void>;
  onRestart: () => void;
}) {
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const verify = useMutation({
    mutationFn: () => trpcClient.auth.twoFactor.verify.mutate({ challenge, code }),
    onSuccess: onSignedIn,
  });
  const switchKind = () => {
    setRecovery(!recovery);
    setCode('');
    verify.reset();
  };
  return (
    <AuthFrame title="Two-factor sign-in">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          verify.mutate();
        }}
        className="space-y-4"
      >
        {verify.error && <ErrorBanner>{errorMessage(verify.error)}</ErrorBanner>}
        <p className="text-sm text-slate-600">
          {recovery
            ? 'Enter one of the recovery codes you saved when you set up two-factor sign-in. Each works once.'
            : 'Enter the six-digit code your authenticator app shows for this account.'}
        </p>
        <div>
          <Label htmlFor="two-factor-code">
            {recovery ? 'Recovery code' : 'Code from your authenticator app'}
          </Label>
          <CodeInput recovery={recovery} value={code} onChange={setCode} />
        </div>
        <Button type="submit" variant="primary" className="w-full" busy={verify.isPending}>
          Verify
        </Button>
        <div className="flex justify-between gap-2">
          <Button size="sm" variant="ghost" onClick={switchKind}>
            {recovery ? 'Use your authenticator app' : 'Use a recovery code instead'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onRestart}>
            Start again
          </Button>
        </div>
      </form>
    </AuthFrame>
  );
}

function CodeInput({
  recovery = false,
  value,
  onChange,
}: {
  recovery?: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Input
      key={recovery ? 'recovery' : 'app'}
      id="two-factor-code"
      required
      autoFocus
      spellCheck={false}
      autoComplete={recovery ? 'off' : 'one-time-code'}
      inputMode={recovery ? 'text' : 'numeric'}
      maxLength={recovery ? 40 : 10}
      className="font-mono tracking-wider"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** A secret in groups of four, as it is easiest to read and type. */
function inFours(secret: string): string {
  return secret.match(/.{1,4}/g)?.join(' ') ?? secret;
}

/**
 * The otpauth link as a QR code, drawn by the server in SVG. Shown as an image, so nothing in
 * the SVG can run.
 */
function TwoFactorQr({ svg }: { svg: string }) {
  return (
    <div data-testid="two-factor-qr" className="flex justify-center">
      <img
        src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`}
        alt="QR code for your authenticator app"
        className="size-44"
      />
    </div>
  );
}

/** The first time: add the secret to an app, and turn two-factor sign-in on with its first code. */
function EnrollStep({
  setup,
  onEnrolled,
  onRestart,
}: {
  setup: Setup;
  onEnrolled: (result: Enrolled) => void;
  onRestart: () => void;
}) {
  const product = useBranding()?.product_name ?? '';
  const [code, setCode] = useState('');
  const enroll = useMutation({
    mutationFn: () => trpcClient.auth.twoFactor.enroll.mutate({ challenge: setup.challenge, code }),
    onSuccess: onEnrolled,
  });
  return (
    <AuthFrame title="Set up two-factor sign-in" wide>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          enroll.mutate();
        }}
        className="space-y-4"
      >
        {enroll.error && <ErrorBanner>{errorMessage(enroll.error)}</ErrorBanner>}
        <p className="text-sm text-slate-600">
          {product} asks everyone for a code from an authenticator app as well as their password.
          Add this account to an app such as Google Authenticator, Microsoft Authenticator or
          1Password by scanning the QR code or typing in the key, then enter the code the app shows.
        </p>
        <TwoFactorQr svg={setup.qr_svg} />
        <div>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-600">Key</span>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Copy key"
              onClick={() => void navigator.clipboard?.writeText(setup.secret)}
            >
              <Copy className="size-4" aria-hidden />
            </Button>
          </div>
          <code
            data-testid="two-factor-secret"
            className="block rounded-md bg-slate-100 px-3 py-2 text-center font-mono text-sm tracking-wider break-words text-slate-900 select-all"
          >
            {inFours(setup.secret)}
          </code>
          <p className="mt-1.5 text-xs text-slate-500">
            On a phone with an authenticator app,{' '}
            <a href={setup.otpauth_uri} className="text-brand-strong hover:underline">
              open this link
            </a>{' '}
            to add the account in one step.
          </p>
        </div>
        <div>
          <Label htmlFor="two-factor-code">Code from your authenticator app</Label>
          <CodeInput value={code} onChange={setCode} />
        </div>
        <Button type="submit" variant="primary" className="w-full" busy={enroll.isPending}>
          Turn on two-factor sign-in
        </Button>
        <Button size="sm" variant="ghost" onClick={onRestart}>
          Start again
        </Button>
      </form>
    </AuthFrame>
  );
}

/** The recovery codes, once, and a confirmation that they are kept before the app opens. */
function SaveCodesStep({
  codes,
  account,
  onContinue,
}: {
  codes: string[];
  account: string;
  onContinue: () => Promise<void>;
}) {
  const [saved, setSaved] = useState(false);
  const [leaving, setLeaving] = useState(false);
  return (
    <AuthFrame title="Save your recovery codes" wide>
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Two-factor sign-in is on. If you lose your phone, each of these codes signs you in once in
          place of a code from the app. Keep them somewhere safe, such as a password manager: this
          is the only time they are shown.
        </p>
        <RecoveryCodes codes={codes} account={account} />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="size-4 rounded border-slate-300"
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
          />
          I have saved my recovery codes
        </label>
        <Button
          variant="primary"
          className="w-full"
          disabled={!saved}
          busy={leaving}
          onClick={() => {
            setLeaving(true);
            void onContinue();
          }}
        >
          Continue
        </Button>
      </div>
    </AuthFrame>
  );
}

export function AcceptInviteScreen() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const accept = useMutation({
    mutationFn: () => trpcClient.auth.acceptInvite.mutate({ token: token ?? '', password }),
    onSuccess: () => navigate({ to: '/login' }),
  });
  return (
    <AuthFrame title="Choose your password">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          accept.mutate();
        }}
        className="space-y-4"
      >
        {!token && (
          <ErrorBanner>
            This link is incomplete. Open the link from your invitation email again.
          </ErrorBanner>
        )}
        {accept.error && <ErrorBanner>{errorMessage(accept.error)}</ErrorBanner>}
        <div>
          <Label htmlFor="new-password">Password</Label>
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500">
            At least 12 characters. A few unrelated words make a good one. When you first sign in,
            you also set up an authenticator app.
          </p>
        </div>
        <Button
          type="submit"
          variant="primary"
          className="w-full"
          busy={accept.isPending}
          disabled={!token}
        >
          Set password
        </Button>
      </form>
    </AuthFrame>
  );
}
